import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { useProgress } from '../../context/ProgressContext';
import { 
  getCourseById, 
  completeLectureApi, 
  submitLessonQuizApi, 
  submitFinalQuizApi, 
  getMyEnrollmentsApi
} from '../../api/apiServices';
import ProgressBar from '../../components/base/ProgressBar';
import { ScoreBadge } from '../../components/base/Badge';

function QuizSection({ 
  questions, 
  quizId, 
  courseId, 
  lessonId, 
  isFinal = false, 
  onComplete 
}) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleAnswer = (qId, idx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qId]: idx }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < questions.length) return;

    setSubmitting(true);

    const correctCount = questions.filter(q => 
      answers[q.id || q._id] === q.correctAnswer
    ).length;
    const score = Math.round((correctCount / questions.length) * 100);
    const passed = score >= 70;

    const result = {
      quizId,
      lessonId,
      courseId,
      score,
      totalQuestions: questions.length,
      correctAnswers: correctCount,
      passed,
      completedAt: new Date().toISOString(),
      isFinalQuiz: isFinal,
    };

    try {
      if (isFinal) {
        await submitFinalQuizApi(courseId, { score, passed });
      } else {
        await submitLessonQuizApi(courseId, lessonId, { score, passed });
      }
      onComplete(result);
    } catch (err) {
      console.error("Failed to submit quiz:", err);
      alert("Failed to submit quiz. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Submitted State - Nice Results Screen
  if (submitted) {
    const correctCount = questions.filter(q => 
      answers[q.id || q._id] === q.correctAnswer
    ).length;
    const score = Math.round((correctCount / questions.length) * 100);

    return (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-10">
        <div className="text-center mb-10">
          <div className={`w-32 h-32 mx-auto rounded-full flex items-center justify-center text-6xl font-bold mb-6 shadow-inner ${
            score >= 70 ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
          }`}>
            {score}%
          </div>
          
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            {score >= 70 ? "Great Job!" : "Quiz Completed"}
          </h2>
          <p className="text-slate-600">
            You got {correctCount} out of {questions.length} correct
          </p>
        </div>

        <div className="space-y-6">
          {questions.map((q, idx) => {
            const userAnswer = answers[q.id || q._id];
            const isCorrect = userAnswer === q.correctAnswer;

            return (
              <div 
                key={idx} 
                className={`p-6 rounded-2xl border transition-all ${
                  isCorrect 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-rose-50 border-rose-200'
                }`}
              >
                <p className="font-semibold text-slate-800 mb-3">{q.text || q.questionText}</p>
                
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-sm font-medium ${isCorrect ? 'text-emerald-600' : 'text-rose-600'}`}>
                    Your Answer: {q.options?.[userAnswer] || 'Not answered'}
                  </span>
                </div>

                {!isCorrect && (
                  <p className="text-sm text-emerald-700 font-medium">
                    Correct Answer: {q.options?.[q.correctAnswer]}
                  </p>
                )}

                {q.explanation && (
                  <p className="text-slate-600 text-sm mt-4 border-t border-slate-100 pt-4">
                    {q.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQ];

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
      <div className="flex justify-between items-center mb-8">
        <h3 className="text-2xl font-bold text-slate-900">
          {isFinal ? 'Final Course Quiz' : 'Lesson Quiz'}
        </h3>
        <div className="px-4 py-1.5 bg-slate-100 rounded-full text-sm font-medium text-slate-600">
          Question {currentQ + 1} of {questions.length}
        </div>
      </div>

      <ProgressBar 
        value={((currentQ + 1) / questions.length) * 100} 
        size="lg" 
        className="mb-10" 
      />

      <div className="mb-8">
        <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
          {currentQuestion?.type === 'true-false' ? 'TRUE / FALSE' : 'MULTIPLE CHOICE'}
        </span>
      </div>

      <p className="text-xl font-semibold text-slate-800 leading-relaxed mb-10">
        {currentQuestion?.text || currentQuestion?.questionText}
      </p>

      <div className="space-y-4 mb-12">
        {(currentQuestion?.options || []).map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(currentQuestion.id || currentQuestion._id, idx)}
            className={`w-full text-left p-5 rounded-2xl border-2 transition-all text-lg ${
              answers[currentQuestion.id || currentQuestion._id] === idx
                ? 'border-teal-500 bg-teal-50 text-teal-800 shadow-sm'
                : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
            }`}
          >
            <span className="font-mono text-base mr-4 text-slate-400">
              {String.fromCharCode(65 + idx)}
            </span>
            {option}
          </button>
        ))}
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => setCurrentQ(c => Math.max(0, c - 1))}
          disabled={currentQ === 0}
          className="px-8 py-3 text-sm font-medium border border-slate-300 rounded-2xl disabled:opacity-40 hover:bg-slate-50"
        >
          Previous
        </button>

        {currentQ < questions.length - 1 ? (
          <button
            onClick={() => setCurrentQ(c => c + 1)}
            disabled={answers[currentQuestion.id || currentQuestion._id] === undefined}
            className="px-10 py-3 bg-teal-600 text-white font-semibold rounded-2xl disabled:bg-slate-200 disabled:text-slate-400 hover:bg-teal-700 transition"
          >
            Next Question
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={Object.keys(answers).length < questions.length || submitting}
            className="px-10 py-3 bg-teal-600 text-white font-semibold rounded-2xl disabled:bg-slate-200 disabled:text-slate-400 hover:bg-teal-700 transition"
          >
            {submitting ? 'Submitting...' : 'Submit Quiz'}
          </button>
        )}
      </div>
    </div>
  );
}

function SlideViewer({ slides }) {
  const [current, setCurrent] = useState(0);
  const slide = slides[current];

  return (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
      <div className="relative aspect-video bg-slate-950">
        {slide?.imageUrl && (
          <img 
            src={slide.imageUrl.startsWith('http') 
              ? slide.imageUrl 
              : `http://localhost:30022/${slide.imageUrl}`} 
            alt={slide.caption || `Slide ${current + 1}`} 
            className="w-full h-full object-contain" 
          />
        )}
        
        {slide?.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
            <p className="text-white text-lg font-medium">{slide.caption}</p>
          </div>
        )}
      </div>

      <div className="p-6 flex items-center justify-between bg-slate-50">
        <button 
          onClick={() => setCurrent(c => Math.max(0, c - 1))} 
          disabled={current === 0}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl border border-slate-200 disabled:opacity-40 hover:bg-white transition"
        >
          ← Previous
        </button>

        <div className="text-center">
          <span className="text-sm text-slate-500 font-medium">
            Slide {current + 1} of {slides.length}
          </span>
        </div>

        <button 
          onClick={() => setCurrent(c => Math.min(slides.length - 1, c + 1))} 
          disabled={current === slides.length - 1}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl border border-slate-200 disabled:opacity-40 hover:bg-white transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default function LessonViewerPage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const { 
    completeLesson, 
    isLessonCompleted, 
    saveQuizResult, 
    getQuizResult, 
    completeCourse 
  } = useProgress();

  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [activeView, setActiveView] = useState('content'); // 'content' or 'quiz'
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);

  // Load course
  useEffect(() => {
    const loadLesson = async () => {
      if (!courseId || !lessonId) return;

      try {
        setLoading(true);

        const courseRes = await getCourseById(courseId);
        if (!courseRes.success || !courseRes.course) {
          navigate(`/courses/${courseId}`);
          return;
        }

        const fetchedCourse = courseRes.course;
        setCourse(fetchedCourse);

        let foundLesson = null;
        fetchedCourse.sections?.forEach(section => {
          const lecture = section.lectures?.find(l => String(l._id) === String(lessonId));
          if (lecture) foundLesson = lecture;
        });

        if (!foundLesson) {
          navigate(`/courses/${courseId}`);
          return;
        }

        setCurrentLesson(foundLesson);

        if (isAuthenticated) {
          const enrollRes = await getMyEnrollmentsApi();
          const isUserEnrolled = enrollRes.success && 
            enrollRes.enrollments?.some(e => 
              String(e.courseId?._id || e.courseId) === String(courseId)
            );
          setIsEnrolled(isUserEnrolled);

          if (!isUserEnrolled) navigate(`/courses/${courseId}`, { replace: true });
        }
      } catch (err) {
        console.error(err);
        navigate(`/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [courseId, lessonId, isAuthenticated, navigate]);

  if (loading || !course || !currentLesson) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Loading lesson...</p>
      </div>
    );
  }

  if (!isEnrolled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Redirecting...</p>
      </div>
    );
  }

  // ====================== VALIDATION ======================
  const getPreviousLesson = () => {
    for (let section of course.sections || []) {
      const lectures = section.lectures || [];
      const idx = lectures.findIndex(l => String(l._id) === String(currentLesson._id));
      if (idx > 0) return lectures[idx - 1];
    }
    return null;
  };

  const prevLesson = getPreviousLesson();

  const canGoToNextLesson = () => {
    if (!prevLesson) return true;
    if (!prevLesson.quiz?.questions?.length) return true;
    return !!getQuizResult(prevLesson._id);
  };

  const canMarkAsComplete = () => {
    if (!currentLesson.quiz?.questions?.length) return true;
    return !!lessonQuizResult;
  };

  // ====================== CALCULATIONS ======================
  const totalLessons = course.sections?.reduce((acc, s) => acc + (s.lectures?.length || 0), 0) || 0;
  const completedLessonsCount = course.sections?.reduce((acc, section) => {
    return acc + (section.lectures?.filter(lec => isLessonCompleted(course._id, lec._id)).length || 0);
  }, 0) || 0;

  const allLessonsViewed = completedLessonsCount === totalLessons;
  const allLessonQuizzesDone = course.sections?.every(section =>
    section.lectures?.every(lecture => {
      if (!lecture.quiz?.questions?.length) return true;
      return !!getQuizResult(lecture._id);
    })
  ) || false;

  const canTakeFinalQuiz = allLessonsViewed && allLessonQuizzesDone;
  const progress = totalLessons > 0 ? Math.round((completedLessonsCount / totalLessons) * 100) : 0;

  const lessonQuizResult = getQuizResult(currentLesson._id);
  const finalQuizResult = getQuizResult(course.finalQuiz?._id);

  let nextLesson = null;
  for (let section of course.sections || []) {
    const lectures = section.lectures || [];
    const idx = lectures.findIndex(l => String(l._id) === String(currentLesson._id));
    if (idx !== -1 && idx < lectures.length - 1) {
      nextLesson = lectures[idx + 1];
      break;
    }
  }

  const handleMarkComplete = async () => {
    if (!canMarkAsComplete()) {
      alert("Please complete the lesson quiz first.");
      return;
    }
    try {
      const res = await completeLectureApi(course._id, currentLesson._id);
      if (res.success) {
        completeLesson(course._id, currentLesson._id);
        setActiveView('quiz');
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to complete lesson");
    }
  };

  const handleQuizComplete = (result) => {
    saveQuizResult(result);
    if (result.isFinalQuiz) {
      completeCourse(course._id);
      setTimeout(() => navigate(`/certificate/${course._id}`), 1500);
    } else {
      setActiveView('content');
      completeLesson(course._id, currentLesson._id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(`/courses/${course._id}`)}
              className="text-slate-500 hover:text-slate-700"
            >
              ← Back to Course
            </button>
            <div>
              <p className="text-sm text-slate-500">{course.title}</p>
              <p className="font-semibold text-slate-900">{currentLesson.title}</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-xs text-slate-500">Progress</div>
              <div className="font-medium text-teal-600">{progress}%</div>
            </div>
            <div className="w-32">
              <ProgressBar value={progress} size="sm" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-slate-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveView('content')}
                className={`py-4 font-medium border-b-2 transition-colors ${activeView === 'content' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                Lesson Content
              </button>
              <button
                onClick={() => setActiveView('quiz')}
                className={`py-4 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeView === 'quiz' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                Quiz
                {lessonQuizResult && <span className="text-emerald-500 text-lg">✓</span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Main Content Area */}
          <div className="lg:col-span-8">
            {activeView === 'content' && (
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                {currentLesson.type === 'video' && currentLesson.videoUrl && (
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden mb-8">
                    <iframe 
                      src={currentLesson.videoUrl} 
                      title={currentLesson.title}
                      className="w-full h-full" 
                      allowFullScreen 
                    />
                  </div>
                )}

                {currentLesson.type === 'slides' && currentLesson.slides?.length > 0 && (
                  <SlideViewer slides={currentLesson.slides} />
                )}

                {currentLesson.type === 'text' && currentLesson.textContent && (
                  <div className="prose max-w-none text-slate-700 leading-relaxed">
                    <pre className="whitespace-pre-wrap font-sans">{currentLesson.textContent}</pre>
                  </div>
                )}

                <div className="mt-10">
                  <button 
                    onClick={handleMarkComplete}
                    disabled={!canMarkAsComplete()}
                    className={`w-full py-4 text-lg font-semibold rounded-2xl transition-all ${
                      canMarkAsComplete() 
                        ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {canMarkAsComplete() ? "Mark Lesson as Completed" : "Complete Quiz to Unlock"}
                  </button>
                </div>
              </div>
            )}

            {activeView === 'quiz' && (
              lessonQuizResult ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-10 text-center">
                  <ScoreBadge score={lessonQuizResult.score} />
                  <h2 className="text-3xl font-bold mt-6">Quiz Completed!</h2>
                  <p className="text-slate-600 mt-2">Score: {lessonQuizResult.score}%</p>
                </div>
              ) : (
                <QuizSection
                  questions={currentLesson.quiz?.questions || []}
                  courseId={course._id}
                  lessonId={currentLesson._id}
                  isFinal={false}
                  onComplete={handleQuizComplete}
                />
              )
            )}
          </div>

          {/* Sidebar - Lesson Progress */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-3xl border border-slate-100 p-6 sticky top-24">
              <h3 className="font-semibold text-slate-900 mb-4">Course Progress</h3>
              <div className="mb-6">
                <ProgressBar value={progress} showLabel size="lg" />
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Lessons Completed</span>
                  <span className="font-medium">{completedLessonsCount} / {totalLessons}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Quizzes Passed</span>
                  <span className="font-medium">
                    {course.sections?.filter(s => 
                      s.lectures?.every(l => !l.quiz?.questions?.length || getQuizResult(l._id))
                    ).length || 0}
                  </span>
                </div>
              </div>

              {/* Next Lesson Preview */}
              {nextLesson && (
                <div className="mt-8 pt-6 border-t">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Next Lesson</p>
                  <p className="font-medium text-slate-800">{nextLesson.title}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// export default function LessonViewerPage() {
//   const { courseId, lessonId } = useParams();
//   const navigate = useNavigate();
//   const { isAuthenticated } = useAuth();

//   const { 
//     completeLesson, 
//     saveQuizResult, 
//     getQuizResult, 
//     completeCourse 
//   } = useProgress();

//   const [course, setCourse] = useState(null);
//   const [currentLesson, setCurrentLesson] = useState(null);
//   const [activeView, setActiveView] = useState('content');
//   const [showFinalQuiz, setShowFinalQuiz] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [isEnrolled, setIsEnrolled] = useState(false);

//   // Load course and verify enrollment
//   useEffect(() => {
//     const loadLesson = async () => {
//       if (!courseId || !lessonId) return;

//       try {
//         setLoading(true);

//         // 1. Fetch course
//         const courseRes = await getCourseById(courseId);
//         if (!courseRes.success || !courseRes.course) {
//           navigate(`/courses/${courseId}`);
//           return;
//         }

//         const fetchedCourse = courseRes.course;
//         setCourse(fetchedCourse);

//         // 2. Find current lesson
//         let foundLesson = null;
//         fetchedCourse.sections?.forEach(section => {
//           const lecture = section.lectures?.find(l => l._id === lessonId);
//           if (lecture) foundLesson = lecture;
//         });
//         setCurrentLesson(foundLesson);

//         // 3. Check enrollment from backend (most reliable)
//         if (isAuthenticated) {
//           const enrollRes = await getMyEnrollmentsApi();
//           const isUserEnrolled = enrollRes.success && 
//             enrollRes.enrollments?.some(e => 
//               String(e.courseId?._id || e.courseId) === String(courseId)
//             );

//           setIsEnrolled(isUserEnrolled);

//           if (!isUserEnrolled) {
//             navigate(`/courses/${courseId}`, { replace: true });
//           }
//         }
//       } catch (err) {
//         console.error("Failed to load lesson:", err);
//         navigate(`/courses/${courseId}`);
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadLesson();
//   }, [courseId, lessonId, isAuthenticated, navigate]);

//   if (loading || !course || !currentLesson) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-slate-50">
//         <p className="text-slate-500">Loading lesson...</p>
//       </div>
//     );
//   }

//   if (!isEnrolled) {
//     return (
//       <div className="min-h-screen flex items-center justify-center bg-slate-50">
//         <p className="text-slate-500">Redirecting...</p>
//       </div>
//     );
//   }

//   // ====================== CALCULATIONS ======================
//   const totalLessons = course.sections?.reduce((acc, s) => acc + (s.lectures?.length || 0), 0) || 0;

//   const completedLessonsCount = course.sections?.reduce((acc, section) => {
//     return acc + (section.lectures?.filter(lec => /* your completion logic */ true).length || 0);
//   }, 0) || 0;

//   const allLessonsViewed = completedLessonsCount === totalLessons;

//   const allLessonQuizzesDone = course.sections?.every(section =>
//     section.lectures?.every(lecture => {
//       if (!lecture.quiz?.questions?.length) return true;
//       return !!getQuizResult(lecture._id);
//     })
//   ) || false;

//   const canTakeFinalQuiz = allLessonsViewed && allLessonQuizzesDone;
//   const progress = totalLessons > 0 ? Math.round((completedLessonsCount / totalLessons) * 100) : 0;

  
//   const lessonQuizResult = getQuizResult(currentLesson._id);
//   const finalQuizResult = getQuizResult(course.finalQuiz?._id);

//   // Navigation
//   let prevLesson = null;
//   let nextLesson = null;

//   for (let section of course.sections || []) {
//     const lectures = section.lectures || [];
//     const idx = lectures.findIndex(l => l._id === currentLesson._id);
//     if (idx !== -1) {
//       prevLesson = idx > 0 ? lectures[idx - 1] : null;
//       nextLesson = idx < lectures.length - 1 ? lectures[idx + 1] : null;
//       break;
//     }
//   }

//   const handleMarkComplete = async () => {
//     try {
//       await completeLectureApi(course._id, currentLesson._id);
//       completeLesson(course._id, currentLesson._id);
//       setActiveView('quiz');
//     } catch (err) {
//       console.error("Failed to mark complete:", err);
//     }
//   };

//   const handleQuizComplete = (result) => {
//     saveQuizResult(result);

//     if (result.isFinalQuiz) {
//       completeCourse(course._id);
//       setTimeout(() => navigate(`/certificate/${course._id}`), 1500);
//     } else {
//       setActiveView('content');
//       completeLesson(course._id, currentLesson._id);
//     }
//   };

//   return (
//     <div className="min-h-screen bg-slate-50 flex flex-col">
//       {/* Top Navigation Bar */}
//       <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3 z-50 sticky top-0">
//         <button 
//           onClick={() => navigate(`/courses/${course._id}`)}
//           className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50"
//         >
//           ←
//         </button>
//         <div className="flex-1 min-w-0">
//           <p className="text-xs text-slate-400 truncate">{course.title}</p>
//           <p className="text-sm font-semibold text-slate-800 truncate">{currentLesson.title}</p>
//         </div>
//         <div className="hidden md:block">
//           <span className="text-xs text-slate-400">{progress}% complete</span>
//           <div className="w-24"><ProgressBar value={progress} size="sm" /></div>
//         </div>
//       </div>

//       <div className="flex flex-1 overflow-hidden">
//         <div className="flex-1 overflow-y-auto p-6">
//           <div className="max-w-3xl mx-auto space-y-6">

//             {/* View Tabs */}
//             <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-100 w-fit">
//               <button 
//                 onClick={() => setActiveView('content')} 
//                 className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${activeView === 'content' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
//               >
//                 Content
//               </button>
//               <button 
//                 onClick={() => setActiveView('quiz')} 
//                 className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${activeView === 'quiz' ? 'bg-teal-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}
//               >
//                 Quiz {lessonQuizResult && <span className="text-emerald-400 ml-1">✓</span>}
//               </button>
//             </div>

//             {/* Content View */}
//             {activeView === 'content' && (
//               <div className="space-y-6">
//                 {currentLesson.type === 'video' && currentLesson.videoUrl && (
//                   <div className="aspect-video bg-black rounded-2xl overflow-hidden">
//                     <iframe 
//                       src={currentLesson.videoUrl} 
//                       title={currentLesson.title}
//                       className="w-full h-full" 
//                       allowFullScreen 
//                     />
//                   </div>
//                 )}

//                 {currentLesson.type === 'slides' && currentLesson.slides?.length > 0 && (
//                   <SlideViewer slides={currentLesson.slides} />
//                 )}

//                 {currentLesson.type === 'text' && currentLesson.textContent && (
//                   <div className="bg-white rounded-2xl border border-slate-100 p-8 prose max-w-none">
//                     <pre className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">
//                       {currentLesson.textContent}
//                     </pre>
//                   </div>
//                 )}

//                 <button 
//                   onClick={handleMarkComplete}
//                   className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-2xl transition-colors"
//                 >
//                   Mark as Completed & Take Quiz
//                 </button>
//               </div>
//             )}

//             {/* Quiz View */}
//             {activeView === 'quiz' && (
//               lessonQuizResult ? (
//                 <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
//                   <ScoreBadge score={lessonQuizResult.score} />
//                   <p className="text-2xl font-bold mt-4">Quiz Completed</p>
//                   <p className="text-slate-600 mt-1">Score: {lessonQuizResult.score}%</p>
//                 </div>
//               ) : (
//                 <QuizSection
//                   questions={currentLesson.quiz?.questions || []}
//                   courseId={course._id}
//                   lessonId={currentLesson._id}
//                   isFinal={false}
//                   onComplete={handleQuizComplete}
//                 />
//               )
//             )}

//             {/* Navigation Buttons */}
//             <div className="flex justify-between pt-6">
//               {prevLesson && (
//                 <button
//                   onClick={() => navigate(`/courses/${course._id}/lesson/${prevLesson._id}`)}
//                   className="flex items-center gap-2 px-5 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-medium hover:bg-slate-50"
//                 >
//                   ← Previous Lesson
//                 </button>
//               )}

//               {nextLesson ? (
//                 <button
//                   onClick={() => navigate(`/courses/${course._id}/lesson/${nextLesson._id}`)}
//                   className="flex items-center gap-2 px-5 py-3 bg-teal-600 text-white rounded-2xl text-sm font-medium hover:bg-teal-700"
//                 >
//                   Next Lesson →
//                 </button>
//               ) : (
//                 canTakeFinalQuiz && !finalQuizResult && (
//                   <button 
//                     onClick={() => setShowFinalQuiz(true)}
//                     className="flex items-center gap-2 px-5 py-3 bg-amber-500 text-white rounded-2xl text-sm font-medium hover:bg-amber-600"
//                   >
//                     <i className="ri-trophy-line" /> Take Final Quiz
//                   </button>
//                 )
//               )}
//             </div>

//             {/* Final Quiz */}
//             {showFinalQuiz && !finalQuizResult && (
//               <QuizSection
//                 questions={course.finalQuiz?.questions || []}
//                 courseId={course._id}
//                 isFinal={true}
//                 onComplete={handleQuizComplete}
//               />
//             )}

//             {/* Course Completion */}
//             {finalQuizResult && (
//               <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
//                 <i className="ri-trophy-fill text-4xl text-amber-500 mb-3" />
//                 <p className="text-xl font-bold text-slate-900">Congratulations! You completed the course</p>
//                 <Link 
//                   to={`/certificate/${course._id}`} 
//                   className="inline-block mt-6 px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700"
//                 >
//                   View Your Certificate
//                 </Link>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }